import {
  Chain,
  Hex,
  PublicClient,
  ResourceNotFoundRpcError,
  TransactionReceipt,
  Transport,
} from 'viem';

import { isChainIdSupported, parseSwap } from '@/lib/0x-parser';
import { ApiError } from '@/lib/errors/api';
import { ClientDataError } from '@/lib/errors/client';

import { BLOCK_CONFIRMATION_TIME_IN_SECONDS } from '@/constants/chain';

import {
  BatchSwapTransaction,
  SerializableTransactionReceipt,
  SwapTransaction,
  Transaction,
  WrapTransaction,
} from '@/store/transaction/types';

import { trackError } from '@/utils/errors';
import { EthereumAddress } from '@/utils/models';
import { sleep } from '@/utils/sleep';

// Override of the viem `waitForTransactionReceipt` default `timeout` (180s) and
// the `waitForCallsStatus` default (60s). A legitimate slow transaction (low gas,
// congestion) can take several minutes to mine; the default would prematurely
// error. On this timeout we commit `not_observed` (the mempool probe does the
// earlier dropped-transaction detection).
export const RECEIPT_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Polling cadence for receipt / calls-status / gasless polling. Scales with the
 * chain's block time (a third of it) but is clamped to 1-2s so fast chains stay
 * responsive and slow chains do not hammer the RPC.
 */
export const computePollingInterval = (chainId: number): number =>
  Math.min(Math.max(((BLOCK_CONFIRMATION_TIME_IN_SECONDS[chainId] ?? 12) * 1000) / 3, 1000), 2000);

/**
 * The transaction variants the receipt watchers resolve directly from an
 * on-chain EVM receipt (gasless, intents, and cross-chain resolve through
 * their own status APIs).
 */
export type ReceiptWatchedTransaction = SwapTransaction | BatchSwapTransaction | WrapTransaction;

export const fetchReceiptWithRetry = async ({
  publicClient,
  hash,
}: {
  publicClient: PublicClient<Transport, Chain>;
  hash: Hex;
}): Promise<TransactionReceipt> => {
  let lastError: unknown;

  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await publicClient.getTransactionReceipt({ hash });
    } catch (err) {
      lastError = err;
      if (attempt < 2) await sleep(computePollingInterval(publicClient.chain.id));
    }
  }

  throw lastError;
};

/**
 * Parses a mined swap, retrying when the trace node has not indexed the block
 * yet. A load-balanced RPC can serve the receipt before every backend has the
 * transaction, so `debug_traceTransaction` briefly answers "not found".
 */
export const parseSwapWithRetry = async (
  args: Parameters<typeof parseSwap>[0],
): ReturnType<typeof parseSwap> => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await parseSwap(args);
    } catch (err) {
      if (attempt >= 2 || !(err instanceof ResourceNotFoundRpcError)) throw err;
      await sleep(computePollingInterval(args.publicClient.chain.id));
    }
  }
};

/**
 * Serializes a transaction receipt (logs/logsBloom already stripped) for the
 * store, converting bigint fields to strings.
 */
export const serializeReceipt = (
  receipt: Omit<TransactionReceipt, 'logs' | 'logsBloom'>,
): SerializableTransactionReceipt => {
  return Object.keys(receipt).reduce<Record<string, string | unknown>>((memo, key) => {
    const val = (receipt as Record<string, unknown>)[key];
    memo[key] = typeof val === 'bigint' ? val.toString() : val;
    return memo;
  }, {}) as SerializableTransactionReceipt;
};

/**
 * Builds the terminal transaction object for a swap / batch-swap / wrap once its
 * receipt is known: serializes the receipt and, for swaps, attaches the parsed
 * swap data. Receipt retrieval happens before this call, and parser failures are
 * non-fatal, so the expected resolution path does not reject.
 */
export const buildResolvedSwapTx = async ({
  tx,
  publicClient,
  hash,
  receipt,
  parse,
  smartContractWallet,
}: {
  tx: ReceiptWatchedTransaction;
  publicClient: PublicClient<Transport, Chain>;
  hash: Hex;
  receipt: TransactionReceipt;
  parse: boolean;
  smartContractWallet?: EthereumAddress;
}): Promise<ReceiptWatchedTransaction> => {
  const { logs: _logs, logsBloom: _logsBloom, ...rest } = receipt;
  const serialized = serializeReceipt(rest);

  const base = {
    ...tx,
    raw: { ...(tx.raw || {}), ...serialized },
  } as ReceiptWatchedTransaction;

  // Skip parsing when not requested, when the transaction did not succeed (a
  // reverted transaction emits no swap logs, so there is nothing to parse), or
  // on chains parseSwap does not support (it would throw "unsupported"); the tx
  // still commits with the raw receipt.
  if (!parse || receipt.status !== 'success' || !isChainIdSupported(publicClient.chain.id)) {
    return base;
  }

  try {
    const parsedTx = await parseSwapWithRetry({
      publicClient,
      transactionHash: hash,
      smartContractWallet,
    });
    if (!parsedTx || parsedTx.tokenIn.amount === '') {
      trackError(new ClientDataError('Failed to parse swap'), {
        txHash: hash,
        chainId: publicClient.chain.id,
      });
    }
    return { ...base, parsedTx };
  } catch (err) {
    if (err instanceof ApiError || err instanceof Error) trackError(err);
    return base;
  }
};

/**
 * Resolves a transaction to its terminal state. The store commit ALWAYS runs so
 * every tab converges on the terminal receipt; `shouldEmit` (the result of the
 * analytics claim) gates only the terminal-analytics emission. `revertReason` is
 * the decoded EVM revert reason, set only for reverted swaps.
 */
export type ResolveHandler = (
  tx: Transaction,
  address: string,
  chainId: number,
  shouldEmit: boolean,
  revertReason?: string,
) => void;

/**
 * Handles a sped-up (repriced) transaction: the hash-retarget store update ALWAYS
 * runs so all tabs converge on the replacing hash; `shouldEmit` gates only the
 * success-analytics emission.
 */
export type RepriceHandler = (
  previousHash: string,
  tx: ReceiptWatchedTransaction,
  address: string,
  chainId: number,
  shouldEmit: boolean,
) => void;

/**
 * Tries to claim terminal-analytics idempotency for a hash before the (possibly async)
 * resolve. Returns true if the caller should emit, false if the analytics were
 * already emitted (this tab or another). Keyed by the watcher's current hash;
 * the store also matches `originalHash` for repriced transactions. The result
 * gates only analytics/parse work — never the store commit.
 */
export type TryClaim = (hash: string, address: string, chainId: number) => boolean;
